"use client";

import { FunnelChart as DubFunnelChart } from "@dub/ui/charts";
import { Tooltip, TooltipProvider } from "@dub/ui";
import { Info } from "lucide-react";

export interface FunnelStep {
  id: string;
  label: string;
  value: number;
  additionalValue?: number;
  colorClassName: string;
  description?: string;
}

export interface FunnelChartProps {
  steps: FunnelStep[];
  /** Height in pixels - required for the chart to render */
  height?: number;
  showLegend?: boolean;
}

// Map text colors to background colors for legend circles
// This ensures Tailwind includes these classes in the build
const colorToBgMap: Record<string, string> = {
  'text-slate-600': 'bg-slate-600',
  'text-amber-600': 'bg-amber-600',
  'text-blue-600': 'bg-blue-600',
  'text-indigo-600': 'bg-indigo-600',
  'text-purple-600': 'bg-purple-600',
  'text-pink-600': 'bg-pink-600',
  'text-emerald-600': 'bg-emerald-600',
  'text-red-600': 'bg-red-600',
  'text-orange-600': 'bg-orange-600',
  'text-cyan-600': 'bg-cyan-600',
  'text-teal-600': 'bg-teal-600',
  'text-green-600': 'bg-green-600',
  'text-gray-600': 'bg-gray-600',
  'text-neutral-600': 'bg-neutral-600',
};

/**
 * FunnelChart wrapper around @dub/ui FunnelChart.
 * The chart requires a container with explicit dimensions to render.
 */
export function FunnelChart({ steps, height = 300, showLegend = false }: FunnelChartProps) {
  const getBgColor = (textColor: string): string => {
    return colorToBgMap[textColor] || 'bg-neutral-400';
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        <div style={{ height }}>
          <DubFunnelChart steps={steps} />
        </div>
        
        {showLegend && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 pt-4 border-t border-neutral-100">
            {steps.map((step) => (
              <div key={step.id} className="flex items-start gap-2">
                <div className={`w-3 h-3 rounded-full mt-1.5 shrink-0 ${getBgColor(step.colorClassName)}`} />
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-neutral-700">{step.label}</span>
                    {step.description && (
                      <Tooltip content={step.description}>
                        <Info className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 transition-colors cursor-help" />
                      </Tooltip>
                    )}
                  </div>
                  <span className="text-xs text-neutral-500">{step.value} funds</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
